import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { OrganizationUserStatus } from "@/enums";
import { UpdateUserInput } from "@/schemas";
import { UserStatusFilter } from "@/hooks/queries";
import { FullUser, PublicUser, PublicUserWithAuditDto } from "@/types/common";

export type CreateUserRequest = UpdateUserInput;
export type UpdateUserRequest = UpdateUserInput;

export type CreateUserOutcome =
  | "invited"
  | "invite_failed"
  | "reconciled_active"
  | "reconciled_pending"
  | "reactivated"
  | "pending_role_not_applied";

export type CreateUserResponse = PublicUser & { outcome: CreateUserOutcome };

/**
 * Creates a new user by sending the provided data to the backend.
 *
 * @param data - The data to create the user with
 * @returns A promise that resolves with the created user data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createUser(
  data: CreateUserRequest
): Promise<CreateUserResponse> {
  try {
    return await apiClient<CreateUserResponse>(`/api/admin/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "createUser");
    throw error;
  }
}

/**
 * Fetches a user by their ID.
 *
 * @param userId - The ID of the user to fetch
 * @returns A promise that resolves with the user data from the backend.
 * @throws Will throw an error if the request fails or if the response is invalid.
 */
export async function fetchUserById(
  userId: string
): Promise<PublicUserWithAuditDto> {
  try {
    return await apiClient<PublicUserWithAuditDto>(
      `/api/admin/users/${userId}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchUserById");
    throw error;
  }
}

/**
 * Fetches all users from the current organization.
 *
 * @returns A promise that resolves to an array of `PublicUser`.
 * @throws Will throw an error if the request fails or if the response is invalid.
 */
export async function fetchPublicUsers(
  status?: UserStatusFilter
): Promise<PublicUser[]> {
  try {
    // Handle array of statuses by joining them with commas
    const statusParam = Array.isArray(status) ? status.join(",") : status;

    return await apiClient<PublicUser[]>(
      `/api/users/all?status=${statusParam}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchUsersByOrganization");
    throw error;
  }
}

/**
 * Fetches all users that belong to a specific organization along with their roles, statuses, and last activity dates.
 * @param organizationId - The ID of the organization to fetch users from
 * @returns An array of FullUser objects representing the users in the organization
 */
export async function fetchFullUsers(): Promise<FullUser[]> {
  try {
    return await apiClient<FullUser[]>(`/api/admin/users/all`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchOrganizationUsersByOrganization");
    throw error;
  }
}

/**
 * Updates an existing user by sending the provided data to the backend.
 *
 * @param userId - The ID of the user to update
 * @param data - The data to update the user with
 * @returns A promise that resolves with the updated user data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateUser(
  userId: string,
  data: UpdateUserRequest
): Promise<PublicUser> {
  try {
    return await apiClient<PublicUser>(`/api/admin/users/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "updateUser");
    throw error;
  }
}

/**
 * Updates an existing user profile by sending the provided data to the backend.
 *
 * @param userId - The ID of the user to update
 * @param data - The data to update the user profile with
 * @returns A promise that resolves with the updated user profile data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateUserProfile(
  userId: string,
  data: UpdateUserRequest
): Promise<PublicUser> {
  try {
    return await apiClient<PublicUser>(`/api/users/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "updateUserProfile");
    throw error;
  }
}

/**
 * Deactivates a user by their ID.
 *
 * @param userId - The ID of the user to deactivate
 * @returns A promise that resolves with the updated user status
 * @throws Will throw an error if the request fails
 */
export async function deactivateUser(
  userId: string,
  reassignedToUserId: string
): Promise<{ id: string; status: OrganizationUserStatus }> {
  try {
    return await apiClient<{ id: string; status: OrganizationUserStatus }>(
      `/api/admin/users/${userId}/deactivate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reassignedToUserId }),
      }
    );
  } catch (error) {
    logError(error, "deactivateUser");
    throw error;
  }
}

/**
 * Reactivates a user by their ID.
 *
 * @param userId - The ID of the user to reactivate
 * @returns A promise that resolves with the updated user status
 * @throws Will throw an error if the request fails
 */
export async function reactivateUser(
  userId: string
): Promise<{ id: string; status: OrganizationUserStatus }> {
  try {
    return await apiClient<{ id: string; status: OrganizationUserStatus }>(
      `/api/admin/users/${userId}/reactivate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    logError(error, "reactivateUser");
    throw error;
  }
}

/**
 * Sends an invitation to an existing invited user.
 *
 * @param userId - The ID of the user to send the invitation to
 * @returns A promise that resolves to the result of the send operation
 * @throws Will throw an error if the request fails
 */
export async function sendUserInvitation(
  userId: string
): Promise<{ success: boolean; message: string }> {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      `/api/admin/users/${userId}/send-invitation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    logError(error, "sendUserInvitation");
    throw error;
  }
}

/**
 * What the cancellation actually did in WorkOS. `revokedCount` counts the
 * pending invitations that were revoked, `membershipDeactivated` reports
 * whether an existing organization membership was deactivated, and
 * `membershipDeleted` whether a pending (never accepted) membership was deleted.
 * All can be zero/false: the row may never have had an invitation sent, in which
 * case the membership was only removed in Skrift.
 */
export type CancelUserInvitationResponse = {
  id: string;
  cancelled: boolean;
  revokedCount: number;
  membershipDeactivated: boolean;
  membershipDeleted: boolean;
};

/**
 * Cancels a pending invitation for an invited user.
 *
 * @param userId - The ID of the user whose invitation is being cancelled
 * @returns A promise that resolves to what was cancelled, locally and in WorkOS
 * @throws Will throw an error if the request fails
 */
export async function cancelUserInvitation(
  userId: string
): Promise<CancelUserInvitationResponse> {
  try {
    return await apiClient<CancelUserInvitationResponse>(
      `/api/admin/users/${userId}/cancel-invitation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    logError(error, "cancelUserInvitation");
    throw error;
  }
}
